export const SESSION_GENERATION_GUIDE = `# Session Generation Features Guide

## Overview

VoiceThera's session generation system creates personalized meditation and wellness sessions using advanced AI. Sessions blend user biographies, organizational context, and therapeutic best practices to deliver highly customized audio experiences.

This guide covers:
- Biography integration and transformation
- Session length enforcement
- Practitioner acknowledgment
- Literal script mode
- Organization context integration
- Category-specific features

---

## Biography Integration

### Personal Biography (All Users)

Every user maintains a personal biography that influences session content:

**Purpose:**
- Personalizes meditation scripts to user's specific life circumstances
- Addresses individual stressors, goals, and challenges
- Creates therapeutic relevance and emotional resonance

**Length:** 100-3000 characters recommended

**Example:**
> "I'm a software engineer working remotely, struggling with blurred boundaries between work and personal life. I find myself checking Slack at 11pm and feeling guilty for taking breaks during the day. My goal is to establish healthier boundaries and stop glorifying overwork."

### Employee Biography (Organization Members)

Organization employees have an additional biography layer:

**Purpose:**
- Captures employee-specific role, stressors, and departmental context
- Blends with organization biography for dual-context sessions
- Enables personalization within corporate wellness framework

**Example:**
> "As a customer support lead at TechCorp, I manage a team of 8 reps handling escalated complaints. The constant emotional labor of soothing angry customers while motivating my burned-out team leaves me depleted. I want to develop resilience without becoming emotionally numb."

### Biography Transformation (Anti-Verbatim Policy)

**Critical Rule**: The system **never reads user inputs verbatim**.

When users provide raw biography content like:
> "i work a whole lot these days and definitely need a break"

The system **transforms** this into polished therapeutic language:
> "grinding through relentless workdays, your body screaming for rest"

**Prohibited:**
- ❌ Reading biography quotes word-for-word
- ❌ Using casual fillers: "kinda", "sorta", "a whole lot", "these days", "definitely", "stuff", "things"
- ❌ Parroting user's exact phrasing

**Required:**
- ✅ Extract emotional essence
- ✅ Transform into professional therapeutic voice
- ✅ Rewrite with vivid, precise language
- ✅ Validate thematic presence (not exact word matching)

**Example Transformation:**

| User Input | ❌ Verbatim (Bad) | ✅ Transformed (Good) |
|-----------|-------------------|----------------------|
| "I'm kinda stressed about money stuff" | "You're kinda stressed about money stuff" | "Financial pressure weighing heavy on your chest, that constant knot of worry about bills" |
| "Work has been crazy these days" | "Work has been crazy these days" | "The relentless pace of deadlines crushing you, that frantic energy that follows you home" |
| "I definitely need to sleep better" | "You definitely need to sleep better" | "Your body craves deep, restorative rest—the kind that actually recharges you" |

This policy applies to **all session types**: individual, group, practitioner-client, employee, Zen AF, News Meditation.

---

## Session Length Enforcement

### Strict Length Requirements

VoiceThera enforces **exact session lengths** with strict word count validation:

**Formula**: ~175 words per minute of audio

| Duration | Target Word Count | Acceptable Range (±50 words) |
|----------|------------------|------------------------------|
| 3 min | 525 words | 475-575 words |
| 5 min | 875 words | 825-925 words |
| 7 min | 1225 words | 1175-1275 words |
| 10 min | 1750 words | 1700-1800 words |
| 15 min | 2625 words | 2575-2675 words |

**Why Strict?**
- Ensures users receive full-length sessions they requested
- Prevents premature truncation or incomplete sessions
- Maintains therapeutic pacing and arc
- Critical for paid sessions and client delivery

**Enforcement:**
- Sessions ≥3 minutes: ±50 word window (strict)
- Sessions <3 minutes: More flexible validation
- System **rejects** sessions outside acceptable range
- AI must generate content to exact specifications

### What Happens If Length Requirements Not Met

If generated content is too short:
1. **Session rejected** with error message
2. System attempts regeneration (up to 3 retries)
3. If all retries fail, user sees error and credits refunded
4. **Never delivered incomplete sessions**

**For practitioners**: Short sessions damage client trust and professional credibility. System ensures every session meets promised length.

---

## Practitioner Acknowledgment

### Mandatory Opening Phrase

For **all practitioner-client sessions**, content must acknowledge the practitioner:

**Required phrase** (within first 250-300 characters):
> "This session was created for you by [Practitioner Name]"

**Variations allowed:**
- "This session was created for you by Dr. Sarah Chen"
- "This wellness session was crafted for you by Alex Rodriguez, LMFT"
- "Dr. Jamie Thompson created this meditation for you"

**Placement:** Beginning of session (after any organization acknowledgment)

### Why This Matters

**Client trust**: Clients know their practitioner personally designed the session
**Professional branding**: Reinforces practitioner-client relationship
**Transparency**: Clear attribution of therapeutic work

### Auto-Insertion

If practitioner acknowledgment is missing from generated content:
- System automatically inserts it at the beginning
- Uses practitioner's full name from profile
- Includes credentials if available (e.g., "LMFT", "PhD")

**Note**: This applies to practitioner-client sessions only, not personal sessions.

---

## Literal Script Mode

### What Is Literal Script Mode?

Practitioners can provide **exact therapeutic scripts** that the AI must deliver **word-for-word** without modification.

**Use cases:**
- Specific therapeutic methodologies (e.g., DBT skills, somatic experiencing)
- Evidence-based protocols requiring exact language
- Trademarked techniques or interventions
- Client-specific scripts developed over time

### How to Provide Literal Scripts

In the practitioner questionnaire (for any category):

1. Scroll to **"Literal Script (Optional)"** section
2. Paste your exact script in the textarea
3. In **"Script Placement Instructions"** sub-section, specify:
   - "Use this as the exact opening"
   - "Weave this throughout the middle section"
   - "Use this verbatim for the closing"
   - "Replace the entire session with this script"

**Example:**

**Literal Script:**
> "Notice your breath. In through the nose, out through the mouth. With each exhale, release the tension in your jaw. Let your tongue rest gently against the roof of your mouth. Soften your eye sockets. Feel gravity holding you."

**Placement Instructions:**
> "Use this as the exact opening, then transition into the client's biography content for the middle section."

### Binary Mode Switch

When literal scripts are provided:

**Standard Mode (no literal script):**
- AI blends biography, organization context, therapeutic techniques
- Transforms user inputs per anti-verbatim policy
- Applies session length enforcement
- Full personalization engine active

**Literal Script Mode (script provided):**
- **Entire prompt replaced** with minimal instruction set
- AI instructed to deliver script **word-for-word**
- No transformation, personalization, or length validation
- Script placement instructions honored exactly

**Why binary?**
Conflicting instructions cause partial delivery. Literal mode ensures scripts are truly verbatim.

### Length Validation Bypass

When literal scripts are provided:
- ✅ Session length validation **disabled**
- ✅ Word count enforcement **bypassed**
- ✅ Script accepted regardless of length
- ✅ No retry logic or validation errors

**Rationale**: Practitioners craft scripts to exact therapeutic specifications. Arbitrary word counts contradict the purpose of literal script mode.

### When to Use Literal Scripts

**Good use cases:**
- ✅ Delivering specific therapeutic protocols
- ✅ Maintaining consistency across client sessions
- ✅ Using evidence-based scripts from training
- ✅ Client-specific language developed collaboratively

**Poor use cases:**
- ❌ Wanting slight customization (use session directives instead)
- ❌ One-off experiments (wastes AI personalization power)
- ❌ Avoiding writing biography (defeats personalization purpose)

---

## Organization Context Integration

### Employee-Mode Sessions

When organization employees create sessions in **employee mode**:

**Organization details automatically included:**
- Organization biography (up to 5000 chars)
- Core values
- Mission statement
- Culture description
- Wellness philosophy

**Session requirements:**
1. **Mandatory opening acknowledgment** (first 250-300 characters):
   > "This wellness moment is brought to you by [Organization Name]"

2. **3-4 explicit connections** between employee frustrations and organizational values:
   - Distributed across opening (20%), middle (60%), closing (20%)
   - Concrete examples, not vague references
   - Shows organizational support is authentic, not performative

3. **Dual empowerment**:
   - Validates individual employee's personal struggles
   - Demonstrates organization cares about their wellbeing
   - Connects personal growth to organizational mission

### Integration Strength Examples

**❌ Weak (Superficial Acknowledgment):**
> "This wellness moment is brought to you by TechCorp. Now let's focus on your stress..."

**✅ Strong (Deep Integration):**
> "This wellness moment is brought to you by TechCorp. You're grinding through customer complaints, that emotional weight of soothing angry people while your own team is burned out. TechCorp literally has 'Champion Belonging' as a core value—they WANT you to speak up about this burden. Your company gets it: protecting your sanity isn't self-indulgence, it's 'Doing the Right Thing.' They brought you this session because they know your wellbeing serves the mission."

**Key differences:**
- Weak: Organization mentioned once, then ignored
- Strong: Organization values woven throughout, specific examples, employee + org empowered

### Validation Requirements

AI prompt explicitly states session will be **rejected** if:
- ❌ Organization name absent from opening
- ❌ Fewer than 3 connections to organizational values
- ❌ Only employee OR organization empowered (not both)
- ❌ Organizational context crammed into one section (not distributed)
- ❌ Session fails to demonstrate understanding of organization's mission

### Personal Mode (Unchanged)

When organization employees create sessions in **personal mode**:
- ❌ **Zero** organization context
- ✅ Only personal biography used
- ✅ Standard individual session generation
- ✅ Personal credit pool used

**Critical**: Personal mode sessions are **completely untouched** by organization features.

---

## Category-Specific Features

### All Categories Support Organization Context

When in employee mode, **every category** integrates organization context:

**Standard Categories:**
- Sleep, Focus, Stress Relief, Anxiety, Confidence, etc.
- Blend organizational values with category-specific therapeutic goals

**Zen AF:**
- Explicit, raw therapeutic language
- Organization values expressed in direct, unfiltered voice
- Example: "Your company's 'Champion Belonging' value? They mean it. They're not paying for your session so you can keep people-pleasing your way into burnout."

**News Meditation:**
- Current events blended with organizational mission
- Connects world news to organizational culture and values
- Example: If organization values sustainability and news covers climate, session addresses climate anxiety through org's environmental commitment

### Zen AF (Explicit Language Mode)

**Characteristics:**
- Profanity allowed and encouraged for therapeutic impact
- No spiritual bypassing or toxic positivity
- Direct, confrontational language
- Validates anger and frustration without sanitizing

**Example phrases:**
- "That voice saying you're not good enough? Fire it. Seriously, tell that inner critic to f*** off."
- "You don't have to 'find the lesson' in trauma. Sometimes bad sh*t just happens."
- "Stop performing productivity. Your worth isn't measured in output, and it's bulls**t that anyone made you believe it was."

**Organization context in Zen AF:**
- Organizational values expressed in explicit, direct language
- No corporate speak or sanitization
- Authentic, raw validation of employee struggles

### News Meditation

**Characteristics:**
- Integrates current events (user provides news article or headline)
- Processes collective anxiety, grief, hope related to news
- Grounds overwhelming global issues in personal agency

**How it works:**
1. User provides news headline or summary
2. AI analyzes emotional impact (fear, anger, helplessness, etc.)
3. Session validates feelings, provides grounding, offers micro-actions
4. Avoids toxic positivity ("everything happens for a reason")

**Organization context in News Meditation:**
- Connects current events to organizational mission/values
- Example: If organization values social justice and news covers inequality, session addresses how employee can embody those values
- Validates employee's emotional response through organizational commitment

### Standard Meditation Categories

**Sleep, Focus, Stress Relief, Anxiety, Confidence, etc.**

Each category has specific therapeutic approaches:

**Sleep:**
- Progressive muscle relaxation
- Body scan techniques
- Soothing imagery and soundscapes
- Slowed pacing and whisper-like delivery

**Focus:**
- Attention training exercises
- Distraction management strategies
- Pomodoro-inspired pacing
- Task reframing and motivation

**Stress Relief:**
- Breathwork (4-7-8, box breathing)
- Somatic release (jaw unclenching, shoulder drops)
- Cognitive reframing of stressors
- Grounding in present moment

**Anxiety:**
- Validates anxiety without pathologizing
- Nervous system regulation techniques
- Cognitive defusion strategies
- Builds sense of safety and control

**Confidence:**
- Reframes imposter syndrome
- Validates accomplishments and competence
- Challenges negative self-talk
- Builds self-compassion and assertion

Organization context integrates seamlessly with category goals.

---

## Content Cache

### How Content Cache Works

To improve generation speed and reduce costs, VoiceThera caches similar session content:

**Cache key includes:**
- Category ID
- Session type
- Prompt keywords (extracted from biography)
- Duration

**When cache is used:**
- User requests session very similar to recent one
- Cache entry not expired (30-day TTL)
- Keywords match threshold (>70% overlap)

**When cache is bypassed:**
- Biography significantly different
- Organization context present (always unique)
- Literal script provided
- User explicitly requests "regenerate"

**User control:**
- "Regenerate" button forces fresh generation (bypasses cache)
- Cache only used for standard categories, not Zen AF or News Meditation

---

## Audio Generation Pipeline

### Text-to-Speech Process

1. **Content Generation**: AI generates markdown script
2. **Text Preprocessing**: Remove markdown, normalize punctuation
3. **Chunking**: Split into manageable audio segments
4. **TTS Synthesis**: Convert text to speech (voice selection based on category)
5. **Audio Mixing**: Add background music, adjust pacing
6. **Quality Check**: Validate audio length matches content length
7. **Storage**: Upload to secure CDN, generate shareable URL

**Voice Selection:**
- **Standard categories**: Warm, calm, measured voice
- **Zen AF**: More expressive, conversational voice
- **Sleep**: Whisper-like, slower pacing
- **Focus**: Energetic, clear enunciation

### Background Music

Users can select optional background music:
- Nature sounds (rain, ocean, forest)
- Ambient music (soft piano, strings)
- Binaural beats (focus, relaxation)
- Silence (no music)

Music volume automatically adjusted to ensure voice clarity.

---

## Frequently Asked Questions

### Biography

**Q: Can I update my biography without affecting old sessions?**
A: Yes, old sessions remain unchanged. Biography changes only affect new sessions.

**Q: How much detail should I include?**
A: More detail = better personalization. Aim for 300-800 characters covering current stressors, goals, and emotional patterns.

**Q: Can I use multiple biographies for different contexts?**
A: Yes, if you have multiple roles (individual, employee at Org A, employee at Org B), each has separate biography.

### Length Enforcement

**Q: Why can't I generate shorter custom sessions?**
A: For sessions ≥3 minutes, strict length ensures quality and completeness. Use standard 1-minute or 2-minute options for brevity.

**Q: What if the AI generates content that's too long?**
A: Session is rejected and regenerated. System ensures content stays within ±50 word window.

### Literal Scripts

**Q: Can I use literal scripts for personal sessions?**
A: No, literal script mode is practitioner-only feature for client sessions.

**Q: Will the AI add anything to my literal script?**
A: No, in literal script mode, AI delivers your script verbatim without additions unless you instruct otherwise in placement instructions.

**Q: Can I mix literal scripts with AI-generated content?**
A: Yes, specify placement: "Use this as opening, then AI-generated middle, then this as closing."

### Organization Context

**Q: Can I disable organization context in employee mode?**
A: No, employee mode always includes organization context. Use personal mode for sessions without organizational framing.

**Q: Will my personal sessions reference my employer?**
A: No, personal mode sessions are completely separate from organization context.

**Q: Can practitioners see organization details?**
A: Only if the organization explicitly grants them access. Practitioners cannot see organizations they haven't been granted access to.

---

## Support

For session generation issues:
- Email: support@voicethera.com
- Chat: In-app support
- Related guides: Client Management, Organization Admin

**Last Updated**: January 2025
**Version**: 1.0
`;
